import { Booking, LoadingPlace, LoadingPlaceTypes } from '../models/booking';
import { Driver } from '../models/driver';
import { Tenant } from '../models/tenant';
import { COUNTRY_NAMES, CountryCode } from '../models/country';

/**
 * Fills the booking template with actual booking and driver data
 */
export const fillBookingTemplate = (
  template: string,
  booking: Booking,
  driver?: Driver,
  vehiclePlate?: string,
  tenant?: Partial<Tenant>,
  trailerPlate?: string
): string => {
  if (!template) return '';

  // Helper to format address
  const formatAddress = (place?: LoadingPlace): string => {
    if (!place?.address) return '';
    const addr = place.address;
    const parts = [
      addr.streetOrPostbox || '',
      `${addr.postalCode || ''} ${addr.city || ''}`.trim(),
      addr.country || '',
    ].filter(p => p !== '');
    return parts.join(', ');
  };

  // Helper to format date/time
  const formatDateTime = (timestamp?: number | string): string => {
    if (!timestamp) return '';
    const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
    return date.toLocaleString();
  };

  // Helper to format date only (no time)
  const formatDate = (value?: number | string): string => {
    if (!value) return '';
    const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
  };

  // Helper to format time only (HH:MM or from datetime string)
  const formatTime = (value?: string): string => {
    if (!value) return '';
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) return value.substring(0, 5);
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  // Helper to resolve country code to full name
  const getCountryName = (code?: string): string => code ? (COUNTRY_NAMES[code as CountryCode] ?? code) : '';

  // Normalize postal code: "BE-2000" → "BE-2000", "2000" + country "BE" → "BE-2000"
  const formatPostalCode = (country?: string, postalCode?: string): string => {
    if (!postalCode) return '';
    // Timocom/marketplace format: "BE-2000" or "FR-75001"
    const prefixed = postalCode.match(/^([A-Z]{2})-(.+)$/);
    if (prefixed) return `${prefixed[1]}-${prefixed[2]}`;
    return country ? `${country}-${postalCode}` : postalCode;
  };

  // Generate dynamic loading/unloading blocks HTML for {{loadingBlocks}}
  const generateLoadingBlocks = (bk: Booking): string => {
    const places = bk.loadingPlaces ?? [];
    const loadingPlaces = places.filter(p => p.loadingType === LoadingPlaceTypes.LOADING);
    const unloadingPlaces = places.filter(p => p.loadingType === LoadingPlaceTypes.UNLOADING);

    // booking.startTime/endTime are typed as number but the API returns ISO strings — String() handles either
    const bkStartStr = bk.startTime ? String(bk.startTime) : undefined;
    const bkEndStr = bk.endTime ? String(bk.endTime) : undefined;

    // Returns "HH:MM - HH:MM" if both differ, "HH:MM" if only start, "Até HH:MM" if only end, or fallback
    const formatTimeRange = (startTime?: string, endTime?: string, fallback?: string): string => {
      const startT = formatTime(startTime);
      const endT = formatTime(endTime);
      if (startT && endT && startT !== endT) return `${startT} - ${endT}`;
      if (startT) return startT;
      if (endT) return `Até ${endT}`;
      return fallback ? formatTime(fallback) : '';
    };

    const renderLoadingBlock = (place: LoadingPlace, index: number): string => {
      const label = loadingPlaces.length > 1 ? `Local de Carga N°${index + 1}:` : 'Local de Carga:';
      const postalCode = formatPostalCode(place.address?.country, place.address?.postalCode);
      const morada = [place.address?.streetOrPostbox, place.address?.city, postalCode, getCountryName(place.address?.country)].filter(Boolean).join(', ');
      // Fall back to booking-level start time for manually created bookings
      const date = formatDate(place.earliestLoadingDate || bkStartStr);
      const time = formatTimeRange(place.startTime, place.endTime, bkStartStr);
      const goodsTypeRow = place.goodsType
        ? `<div class="row"><span class="lbl">Ref n.</span><span class="val">${place.reference ?? ''}</span><span class="spacer"></span><span>Tipo de Carga <span class="val">${place.goodsType}</span></span></div>`
        : `<div class="row"><span class="lbl">Ref n.</span><span class="val">${place.reference ?? ''}</span></div>`;
      return `
    <div class="section-label">${label}</div>
    <div class="section-box">
      <div class="row">
        <span class="lbl">Data:</span><span class="val">${date}</span>
        <span class="lbl" style="margin-left:20px">Hora:</span><span class="val">${time}</span>
        <span class="lbl" style="margin-left:20px">Localidade:</span><span class="val">${getCountryName(place.address?.country)}</span>
      </div>
      <div class="row">
        <span class="lbl">Nome:</span><span class="val">${place.name ?? ''}</span>
      </div>
      <div class="row">
        <span class="lbl">Morada:</span><span class="val">${morada}</span>
      </div>
      <div class="row">
        <span class="lbl">Cod.Postal:</span><span class="val">${postalCode}</span>
      </div>
      ${goodsTypeRow}
    </div>`;
    };

    const renderUnloadingBlock = (place: LoadingPlace, index: number): string => {
      const ordinal = ['1°', '2°', '3°', '4°', '5°'][index] ?? `${index + 1}°`;
      const label = `Destino / ${ordinal} Descarga:`;
      const postalCode = formatPostalCode(place.address?.country, place.address?.postalCode);
      const morada = [place.address?.streetOrPostbox, place.address?.city, postalCode, getCountryName(place.address?.country)].filter(Boolean).join(', ');
      // Fall back to booking-level end time for manually created bookings
      const date = formatDate(place.earliestLoadingDate || bkEndStr);
      const time = formatTimeRange(place.startTime, place.endTime, bkEndStr);
      return `
    <div class="section-label">${label}</div>
    <div class="section-box">
      <div class="row">
        <span class="lbl">Data:</span><span class="val">${date}</span>
        <span class="lbl" style="margin-left:20px">Hora:</span><span class="val">${time}</span>
        <span class="lbl" style="margin-left:20px">Localidade:</span><span class="val">${getCountryName(place.address?.country)}</span>
      </div>
      <div class="row">
        <span class="lbl">Nome:</span><span class="val">${place.name ?? ''}</span>
      </div>
      <div class="row">
        <span class="lbl">Morada:</span><span class="val">${morada}</span>
      </div>
      <div class="row">
        <span class="lbl">Cod.Postal:</span><span class="val">${postalCode}</span>
      </div>
      <div class="row">
        <span class="lbl">Ref n.</span><span class="val">${place.reference ?? ''}</span>
      </div>
    </div>`;
    };

    return [
      ...loadingPlaces.map((p, i) => renderLoadingBlock(p, i)),
      ...unloadingPlaces.map((p, i) => renderUnloadingBlock(p, i)),
    ].join('\n');
  };

  // Get start and end places
  const startPlace = booking.startPlace || booking.loadingPlaces?.find(p => p.loadingType === LoadingPlaceTypes.LOADING);
  const endPlace = booking.endPlace || booking.loadingPlaces?.find(p => p.loadingType === LoadingPlaceTypes.UNLOADING);

  // Get driver name
  const driverName = driver ? `${driver.firstName} ${driver.lastName}`.trim() : '';
  const driverEmail = driver?.email || '';

  // Replace all placeholders
  const filled = template
    .replace(/\{\{bookingId\}\}/g, String(booking.id || ''))
    .replace(/\{\{vehiclePlate\}\}/g, vehiclePlate || '')
    .replace(/\{\{trailerPlate\}\}/g, trailerPlate || '')
    .replace(/\{\{driverName\}\}/g, driverName)
    .replace(/\{\{driverEmail\}\}/g, driverEmail)
    .replace(/\{\{startAddress\}\}/g, formatAddress(startPlace))
    .replace(/\{\{endAddress\}\}/g, formatAddress(endPlace))
    .replace(/\{\{startTime\}\}/g, formatDateTime(booking.startTime))
    .replace(/\{\{endTime\}\}/g, formatDateTime(booking.endTime))
    .replace(/\{\{loadingDate\}\}/g, formatDate((startPlace as any)?.earliestLoadingDate || booking.startTime))
    .replace(/\{\{loadingTime\}\}/g, formatTime(startPlace?.startTime))
    .replace(/\{\{loadingLocality\}\}/g, startPlace?.address?.city || '')
    .replace(/\{\{loadingName\}\}/g, '')
    .replace(/\{\{loadingPostalCode\}\}/g, startPlace?.address?.postalCode || '')
    .replace(/\{\{deliveryDate\}\}/g, formatDate((endPlace as any)?.earliestLoadingDate || booking.endTime))
    .replace(/\{\{deliveryTime\}\}/g, formatTime(endPlace?.startTime))
    .replace(/\{\{deliveryLocality\}\}/g, endPlace?.address?.city || '')
    .replace(/\{\{deliveryName\}\}/g, '')
    .replace(/\{\{deliveryPostalCode\}\}/g, endPlace?.address?.postalCode || '')
    .replace(/\{\{cargoType\}\}/g, '')
    .replace(/\{\{price\}\}/g, booking.price ? `€${booking.price.toFixed(2)}` : '')
    .replace(/\{\{distance\}\}/g, booking.distanceKms ? `${booking.distanceKms} km` : '')
    .replace(/\{\{status\}\}/g, getStatusLabel(booking.status))
    .replace(/\{\{customerDomain\}\}/g, booking.customerDomain || '')
    .replace(/\{\{externalOfferId\}\}/g, booking.externalOfferId || '')
    .replace(/\{\{notificationEmail\}\}/g, tenant?.notificationEmail || '')
    .replace(/\{\{loadingBlocks\}\}/g, generateLoadingBlocks(booking))
    .replace(/\{\{now\}\}/g, new Date().toLocaleString());

  return filled;
};

/**
 * Get status label from booking status enum
 */
const getStatusLabel = (status: number): string => {
  switch (status) {
    case 1: return 'Confirmed';
    case 2: return 'Finished';
    case 3: return 'Cancelled';
    default: return 'Unknown';
  }
};

/**
 * Print the filled HTML via a hidden iframe — no new tab, just the native print dialog.
 * All CSS print features (@page, page-break, fixed footers) work exactly as in the template.
 */
export const printBookingDocument = (htmlContent: string) => {
  // Only inject color-preservation — do NOT override @page settings; the template owns those.
  const colorStyles = '<style>* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }</style>';

  // Extract <title> from the filled template — Chrome uses the parent page's document.title
  // as the PDF filename when printing from an iframe, so we temporarily swap it.
  const titleMatch = htmlContent.match(/<title>([^<]*)<\/title>/i);
  const printTitle = titleMatch?.[1]?.trim() || '';

  let finalContent = htmlContent;
  if (htmlContent.includes('</head>')) {
    finalContent = htmlContent.replace('</head>', colorStyles + '</head>');
  } else {
    finalContent = `<!DOCTYPE html><html><head><title>Loading Sheet</title>${colorStyles}</head><body>${htmlContent}</body></html>`;
  }

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;border:none;opacity:0;';
  document.body.appendChild(iframe);

  const prevTitle = document.title;
  const cleanup = () => {
    document.title = prevTitle;
    if (document.body.contains(iframe)) document.body.removeChild(iframe);
  };

  iframe.addEventListener('load', () => {
    // Small delay lets fonts/images finish rendering before the print dialog opens
    setTimeout(() => {
      if (printTitle) document.title = printTitle;
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      iframe.contentWindow?.addEventListener('afterprint', cleanup, { once: true });
      // Fallback cleanup in case afterprint doesn't fire (e.g. user cancels quickly)
      setTimeout(cleanup, 120_000);
    }, 300);
  }, { once: true });

  iframe.srcdoc = finalContent;
};
